/**
 * <search-page> — the search results page's scope, and the machine-scoped
 * results.
 *
 * Scope "all" is Shopify's search, server-rendered (facet-filters handles its
 * filters and sort). Scope "machine" is the intersection of a machine's
 * collection and the term, computed here: the collection's product feed is
 * loaded in full (every 250-product page), cached per tab, matched token by
 * token, narrowed by an optional category, sorted, and paginated over the
 * complete matching set. Everything shown is in that machine's collection —
 * a verified fit, never a guess from a title.
 *
 * State lives in the URL: q, scope=machine, vehicle=<key>, cat, ptype (many,
 * the product types — `type` is Shopify's own search parameter), brand (many),
 * sort, page. Back and Forward re-render from it; a slow
 * earlier load can never paint over a newer one (each render has a token).
 *
 * The machine scope carries the same sidebar as a collection page — the
 * category tree with a chosen category's product types beneath it, and the
 * brands — built here from the matching parts, in the markup of
 * snippets/facets.liquid so the one stylesheet dresses both (Lucas,
 * 2026-10-10: "use the same side nav on the search results").
 */
(function () {
  'use strict';

  const PER_PAGE = 24;

  const money = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD' }).format(n);
    } catch { return `$${n.toFixed(2)}`; }
  };

  // Matching and the feed are shared with the header's search (collection-feed.js).
  const tokens = (terms) => window.CollectionFeed.tokens(terms);
  const matches = (product, toks) => window.CollectionFeed.matches(product, toks);
  const score = (product, toks) => window.CollectionFeed.score(product, toks);

  /**
   * The products matching the term: every token first; when nothing matches
   * all of them, anything matching some, best matches first ("opera belt"
   * finds the belts — Lucas, 2026-10-12). Returns the list and the tokens
   * that found anything, or null when nothing was loosened.
   */
  function loosen(products, toks) {
    const strict = products.filter((p) => matches(p, toks));
    if (strict.length || toks.length < 2) return { products: strict, loosened: null };
    const scored = products.map((p) => [p, score(p, toks)]).filter(([, n]) => n > 0);
    if (!scored.length) return { products: [], loosened: null };
    scored.sort((a, b) => b[1] - a[1]);
    const found = toks.filter((t) => scored.some(([p]) => score(p, [t]) > 0));
    return { products: scored.map(([p]) => p), loosened: found, rank: new Map(scored.map(([p, n]) => [p.handle, n])) };
  }

  /** The lvl2 category a product carries, from its ZX2_ tag. */
  function categoryOf(product) {
    const tag = (product.tags || []).find((t) => t.startsWith('ZX2_'));
    return tag ? tag.slice(4) : null;
  }

  /** Out of stock after everything in stock, whatever the sort: the one place they still show. */
  const outOfStockLast = (list) => {
    const inStock = (p) => (p.variants || []).some((v) => v.available !== false);
    return list.filter(inStock).concat(list.filter((p) => !inStock(p)));
  };

  function sortProducts(list, sort) {
    const price = (p) => Number((p.variants && p.variants[0] && p.variants[0].price) || 0);
    const copy = list.slice();
    if (sort === 'price-ascending') copy.sort((a, b) => price(a) - price(b));
    else if (sort === 'price-descending') copy.sort((a, b) => price(b) - price(a));
    else if (sort === 'title-ascending') copy.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    // 'relevance' keeps the collection's order, which is best-selling.
    return copy;
  }

  const loadCollection = (key, signal) => window.CollectionFeed.load(key, { signal });

  /** A product card, the same shape as snippets/product-card.liquid. */
  /**
   * The option a card shows — colour as swatches, else size as chips — with
   * one link per value in stock, as snippets/product-card.liquid renders it.
   */
  function cardOptions(product) {
    const opts = product.options || [];
    const colour = opts.find((o) => /^colou?r$/i.test(String(o.name || '')));
    const size = opts.find((o) => /^size$/i.test(String(o.name || '')));
    const option = colour || size;
    if (!option || (product.variants || []).length < 2) return null;
    const kind = colour ? 'color' : 'size';
    const index = (option.position || opts.indexOf(option) + 1) - 1;
    const row = document.createElement('div');
    row.className = `product-card__options product-card__options--${kind}`;
    row.setAttribute('data-card-options', '');
    row.setAttribute('aria-label', option.name);
    for (const value of option.values || []) {
      const pick = (product.variants || []).find((v) => v.available && (v.options || [])[index] === value);
      if (!pick) continue;
      const link = document.createElement('a');
      link.href = `/products/${product.handle}`;
      link.dataset.variant = String(pick.id);
      if (kind === 'color') {
        link.className = 'product-card__swatch';
        link.setAttribute('data-card-swatch', '');
        link.dataset.color = value;
        link.title = value;
        link.setAttribute('aria-label', value);
        if (pick.image) link.dataset.image = `${pick.image}${pick.image.includes('?') ? '&' : '?'}width=400`;
      } else {
        link.className = 'product-card__chip';
        link.setAttribute('data-card-chip', '');
        link.textContent = value;
      }
      row.appendChild(link);
    }
    if (!row.children.length) return null;
    // The label leads, unless the values are many (request 138): past four
    // sizes or six colors it would push the row onto a second line.
    if (row.children.length <= (kind === 'color' ? 6 : 4)) {
      const label = document.createElement('span');
      label.className = 'product-card__options-label';
      label.textContent = kind === 'color' ? 'Available colors:' : 'Available sizes:';
      row.prepend(label);
    }
    return row;
  }

  function card(product) {
    const variant = product.variants[0] || {};
    const single = product.variants.length === 1;
    const price = Number(variant.price || 0);
    const li = document.createElement('li');
    const url = `/products/${product.handle}`;
    const image = product.image ? `${product.image}${product.image.includes('?') ? '&' : '?'}width=400` : '';
    li.innerHTML = `
      <div class="product-card" data-product-handle="">
        <div class="product-card__visual">
        <a class="product-card__media" href="" tabindex="-1" aria-hidden="true">
          ${image ? '<img class="product-card__image product-card__image--primary" loading="lazy" alt="">' : '<span class="product-card__image product-card__image--placeholder"></span>'}
          ${variant.available === false ? '<span class="badge badge--muted product-card__badge">Out of stock</span>' : ''}
        </a>
        <div class="product-card__fits" data-card-fits hidden></div>
        </div>
        <div class="product-card__meta"><span class="product-card__vendor"></span><a class="product-card__mpn" href="" tabindex="-1" data-card-mpn></a></div>
        <a class="product-card__title" href=""></a>
        <div class="product-card__footer">
          <div class="product-card__pricing">
            <span class="product-card__price"></span>
          </div>
          ${variant.available === false
            ? '<a class="product-card__button product-card__button--quiet" href="/products/' + product.handle + '">View details</a>'
            : single
              ? '<form method="post" action="/cart/add" data-card-add><input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="id"><input type="hidden" name="quantity" value="1"><button class="product-card__button" type="submit"><span data-add-label>Add to cart</span></button></form>'
              : '<a class="product-card__button" href="">Choose options</a>'}
        </div>
      </div>`;
    // Customer-facing strings are set as text, never interpolated into markup.
    li.querySelector('.product-card').dataset.productHandle = product.handle;
    li.querySelector('.product-card__media').href = url;
    const img = li.querySelector('img');
    if (img) img.src = image;
    li.querySelector('.product-card__vendor').textContent = product.vendor || '';
    // The MPN is the SKU past its underscore ("12POL_2530009" → "2530009");
    // a link to the product, so WebKit never phone-links it (request 167).
    const mpn = li.querySelector('.product-card__mpn');
    mpn.textContent = String(variant.sku || '').split('_').pop();
    mpn.href = url;
    const title = li.querySelector('.product-card__title');
    title.href = url;
    title.textContent = product.title;
    li.querySelector('.product-card__price').textContent = money(price);
    // No MSRP on a grid card (Lucas, 2026-10-13); the product page shows it.
    const idInput = li.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variant.id);
    const choose = li.querySelector('a.product-card__button');
    if (choose) choose.href = url;
    const options = cardOptions(product);
    if (options) li.querySelector('.product-card__visual').appendChild(options);
    if (window.CardOptions) window.CardOptions.paint(li);
    return li;
  }

  class SearchPage extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.terms = this.dataset.terms || '';
      this.global = this.querySelector('[data-search-global]');
      this.machine = this.querySelector('[data-search-machine]');
      this.scopeSelect = this.querySelector('[data-search-page-scope]');
      this.form = this.querySelector('[data-search-form]');

      this.syncScopeOptions();
      document.addEventListener('garage:changed', () => { this.syncScopeOptions(); this.route(); });

      if (this.scopeSelect) {
        this.scopeSelect.addEventListener('change', () => this.setScope(this.scopeSelect.value));
      }
      if (this.form) {
        // Re-searching keeps the scope: the form carries it as hidden fields.
        this.form.addEventListener('submit', () => this.syncHiddenScope());
      }
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-machine-sort]')) this.setParams({ sort: event.target.value, page: null });
        // A product type or a brand ticked in the sidebar.
        else if (event.target.matches('[data-machine-type]')) this.toggleMany('ptype', event.target.dataset.machineType, event.target.checked);
        else if (event.target.matches('[data-machine-brand]')) this.toggleMany('brand', event.target.dataset.machineBrand, event.target.checked);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.querySelector('[data-machine-facets][data-open]')) this.toggleFacets(false);
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-machine-facets-toggle]')) { this.toggleFacets(true); return; }
        if (event.target.closest('[data-machine-facets-close]')) { this.toggleFacets(false); return; }
        // A category in the tree: one at a time; choosing another, or the
        // open one again, drops its product types with it.
        const branch = event.target.closest('[data-machine-category]');
        if (branch) {
          event.preventDefault();
          const value = branch.dataset.machineCategory;
          this.setParams({ cat: value === this.param('cat') ? null : value, ptype: null, page: null });
          this.toggleFacets(false);
          return;
        }
        const page = event.target.closest('[data-machine-page]');
        if (page) {
          event.preventDefault();
          this.setParams({ page: page.dataset.machinePage === '1' ? null : page.dataset.machinePage });
          this.scrollIntoView({ block: 'start' });
          return;
        }
        const link = event.target.closest('[data-scope-switch], [data-scope-offer-link], [data-machine-link]');
        if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') {
          event.preventDefault();
          history.pushState({}, '', link.getAttribute('href'));
          this.route();
        }
      });
      window.addEventListener('popstate', () => this.route());
      this.route();
    }

    /* ---- URL state ---- */

    params() { return new URLSearchParams(window.location.search); }
    param(name) { return this.params().get(name); }

    url(changes) {
      const p = this.params();
      for (const [k, v] of Object.entries(changes)) {
        p.delete(k);
        if (Array.isArray(v)) { for (const item of v) if (item !== '') p.append(k, item); }
        else if (v !== null && v !== undefined && v !== '') p.set(k, v);
      }
      return `${window.location.pathname}?${p.toString()}`;
    }

    /** Add or drop one value of a many-valued parameter (type, brand). */
    toggleMany(name, value, on) {
      const current = this.params().getAll(name).filter((v) => v !== value);
      if (on) current.push(value);
      this.setParams({ [name]: current, page: null });
    }

    /** The sidebar as a drawer on a phone, like the collection page's. */
    toggleFacets(open) {
      const panel = this.querySelector('[data-machine-facets]');
      if (!panel) return;
      panel.toggleAttribute('data-open', open);
      const toggle = this.querySelector('[data-machine-facets-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    setParams(changes) {
      history.pushState({}, '', this.url(changes));
      this.route();
    }

    setScope(value) {
      if (value === 'all') this.setParams({ scope: null, vehicle: null, cat: null, ptype: null, brand: null, page: null });
      else this.setParams({ scope: 'machine', vehicle: value, cat: null, ptype: null, brand: null, page: null });
    }

    vehicleFor(key) {
      const vehicles = (window.Garage && window.Garage.vehicles) || [];
      return vehicles.find((v) => v.key === key) || null;
    }

    /** The machine the URL asks for, if the garage knows it. */
    scopedVehicle() {
      if (this.param('scope') !== 'machine') return null;
      const key = this.param('vehicle') || (window.Garage && window.Garage.active() && window.Garage.active().key);
      return key ? this.vehicleFor(key) : null;
    }

    /* ---- Scope controls ---- */

    syncScopeOptions() {
      if (!this.scopeSelect) return;
      const vehicles = (window.Garage && window.Garage.vehicles) || [];
      const frag = document.createDocumentFragment();
      for (const v of vehicles) {
        const option = document.createElement('option');
        option.value = v.key;
        option.textContent = v.nickname || v.label;
        frag.appendChild(option);
      }
      const all = document.createElement('option');
      all.value = 'all';
      all.textContent = 'All products';
      frag.appendChild(all);
      this.scopeSelect.replaceChildren(frag);
      const scoped = this.scopedVehicle();
      this.scopeSelect.value = scoped ? scoped.key : 'all';
      this.syncHiddenScope();
    }

    syncHiddenScope() {
      if (!this.form || !this.scopeSelect) return;
      for (const name of ['scope', 'vehicle']) {
        let input = this.form.querySelector(`input[name="${name}"]`);
        const value = this.scopeSelect.value === 'all' ? '' : (name === 'scope' ? 'machine' : this.scopeSelect.value);
        if (!value) { if (input) input.remove(); continue; }
        if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = name; this.form.appendChild(input); }
        input.value = value;
      }
    }

    /**
     * The machine line, as the collection page has it: in the machine scope
     * a barred "Showing only parts for your X." with "Search the entire store
     * instead" beside it; in the store scope, with a machine in the garage, the
     * "Only show parts that fit my X" offer — no pills (Lucas, 2026-10-12).
     */
    paintScopeBar(vehicle) {
      const bar = this.querySelector('[data-search-scope-bar]');
      const chip = this.querySelector('[data-scope-chip]');
      const link = this.querySelector('[data-scope-switch]');
      const offer = this.querySelector('[data-scope-offer]');
      const offerLink = this.querySelector('[data-scope-offer-link]');
      const offerName = this.querySelector('[data-scope-offer-machine]');
      const active = window.Garage && window.Garage.active();
      if (vehicle) {
        // A sentence, then the way out as its own sentence (request 162: the
        // bare link under a bare statement read as a stray).
        if (chip) chip.textContent = `Showing only parts for your ${vehicle.nickname || vehicle.label}.`;
        if (link) {
          link.textContent = 'Search the entire store instead';
          link.href = this.url({ scope: null, vehicle: null, cat: null, page: null });
        }
        if (bar) bar.hidden = false;
        if (offer) offer.hidden = true;
        return;
      }
      if (bar) bar.hidden = true;
      if (!offer) return;
      if (active && offerLink) {
        if (offerName) offerName.textContent = active.nickname || active.label;
        offerLink.href = this.url({ scope: 'machine', vehicle: active.key, cat: null, page: null });
        offer.hidden = false;
      } else {
        offer.hidden = true;
      }
    }

    /**
     * The store scope found nothing for a term of several words: ask Shopify
     * for products matching any of them (its search takes OR) and show those
     * in place, saying so. Keeps the URL; the forms inside carry the
     * loosened term, so a filter or sort keeps it.
     */
    async loosenGlobal() {
      const root = this.global;
      if (!root || !root.querySelector('[data-search-empty]') || root.querySelector('[data-search-loosened]')) return;
      const toks = tokens(this.terms);
      if (toks.length < 2) return;
      const id = root.dataset.sectionId;
      const url = new URL(root.dataset.url || '/search', window.location.origin);
      url.searchParams.set('q', toks.join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('sections', id);
      const token = (this.globalToken = (this.globalToken || 0) + 1);
      try {
        const res = await fetch(url.toString());
        if (!res.ok) return;
        const html = (await res.json())[id];
        if (token !== this.globalToken || !html) return;
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-global]');
        if (!next || next.querySelector('[data-search-empty]')) return;
        const note = document.createElement('p');
        note.className = 'search-page__hint search-page__loosened';
        note.setAttribute('data-search-loosened', '');
        note.textContent = `Nothing matches all of “${this.terms}” — showing products that match any of the words.`;
        root.replaceChildren(note, ...next.childNodes);
      } catch (err) {
        console.error('[search]', err);
      }
    }

    /* ---- Rendering ---- */

    async route() {
      const vehicle = this.scopedVehicle();
      this.paintScopeBar(vehicle);
      if (this.scopeSelect) this.scopeSelect.value = vehicle ? vehicle.key : 'all';
      this.syncHiddenScope();
      this.paintFallback(null);
      if (!vehicle) {
        if (this.global) this.global.hidden = false;
        if (this.machine) this.machine.hidden = true;
        this.loosenGlobal();
        return;
      }
      if (this.global) this.global.hidden = true;
      if (this.machine) this.machine.hidden = false;
      await this.renderMachine(vehicle);
    }

    /**
     * The machine scope found nothing for the term (#120): the search was
     * limited to the machine, so say so and show the store's own results
     * for it — Shopify's, already in the page — in place of the empty
     * machine block. Nothing to show when the store has none either, or
     * when sidebar filters are what narrowed it to nothing.
     */
    paintFallback(vehicle, terms) {
      const note = this.querySelector('[data-search-fallback]');
      if (!note || !this.global) return;
      const storeHasResults = !this.global.querySelector('[data-search-empty]') && !!this.global.querySelector('.product-card');
      if (!vehicle || !storeHasResults) {
        note.hidden = true;
        if (vehicle && this.machine) this.machine.hidden = false;
        return;
      }
      const name = note.querySelector('[data-search-fallback-name]');
      if (name) name.textContent = vehicle.nickname || vehicle.label;
      for (const t of note.querySelectorAll('[data-search-fallback-terms]')) t.textContent = terms;
      note.hidden = false;
      if (this.machine) this.machine.hidden = true;
      this.global.hidden = false;
    }

    async renderMachine(vehicle) {
      const token = (this.token = (this.token || 0) + 1);
      const root = this.machine;
      if (!root) return;
      const status = root.querySelector('[data-machine-status]');
      root.setAttribute('aria-busy', 'true');
      if (status) { status.textContent = `Loading parts for your ${vehicle.label}…`; status.hidden = false; }

      let products;
      try {
        products = await loadCollection(vehicle.key);
      } catch (err) {
        if (token !== this.token) return;
        if (status) { status.textContent = 'Could not load this machine\'s parts. Search the entire store instead.'; status.hidden = false; }
        root.removeAttribute('aria-busy');
        return;
      }
      if (token !== this.token) return; // a newer machine or term took over
      if (status) status.hidden = true;

      const toks = tokens(this.terms);
      const cat = this.param('cat');
      const types = this.params().getAll('ptype');
      const brands = this.params().getAll('brand');
      const sort = this.param('sort') || 'relevance';
      const page = Math.max(1, Number(this.param('page')) || 1);

      const tagged = (p, prefix) => (p.tags || []).filter((t) => t.startsWith(prefix)).map((t) => t.slice(prefix.length));
      const inCategory = (p) => !cat || tagged(p, 'ZX2_').includes(cat);
      const inTypes = (p) => !types.length || tagged(p, 'ZX3_').some((t) => types.includes(t));
      const inBrands = (p) => !brands.length || brands.includes(p.vendor);
      const { products: matched, loosened, rank } = loosen(products, toks);
      const narrowed = matched.filter((p) => inCategory(p) && inTypes(p) && inBrands(p));
      // Counts as a collection page gives them: each category as if chosen
      // (within the brands), each type within the open category, each brand
      // within the category and types.
      const tally = (list, keyOf) => {
        const out = new Map();
        for (const p of list) for (const k of keyOf(p)) out.set(k, (out.get(k) || 0) + 1);
        return out;
      };
      const categories = tally(matched.filter(inBrands), (p) => tagged(p, 'ZX2_'));
      const typeCounts = tally(matched.filter((p) => inCategory(p) && inBrands(p)), (p) => tagged(p, 'ZX3_'));
      const brandCounts = tally(matched.filter((p) => inCategory(p) && inTypes(p)), (p) => (p.vendor ? [p.vendor] : []));
      let ordered = sortProducts(narrowed, sort);
      // Loosened and unsorted: the products matching more of the words first.
      if (rank && sort === 'relevance') ordered = ordered.map((p, i) => [p, i]).sort((a, b) => (rank.get(b[0].handle) - rank.get(a[0].handle)) || (a[1] - b[1])).map(([p]) => p);
      const sorted = outOfStockLast(ordered);
      const pages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
      const current = Math.min(page, pages);
      const slice = sorted.slice((current - 1) * PER_PAGE, current * PER_PAGE);

      const count = root.querySelector('[data-machine-count]');
      const shownTerms = loosened ? loosened.join(' ') : this.terms;
      if (count) {
        count.textContent = this.terms
          ? `${sorted.length} ${sorted.length === 1 ? 'part' : 'parts'} for your ${vehicle.label} match “${shownTerms}”${cat ? ` in ${cat}` : ''}`
          : `${sorted.length} parts for your ${vehicle.label}${cat ? ` in ${cat}` : ''}`;
      }
      if (status && loosened) {
        status.textContent = `Nothing matches all of “${this.terms}” — showing parts that match “${shownTerms}”, best matches first.`;
        status.setAttribute('data-machine-loosened', '');
        status.hidden = false;
      } else if (status) {
        status.removeAttribute('data-machine-loosened');
      }
      const sortSelect = root.querySelector('[data-machine-sort]');
      if (sortSelect) sortSelect.value = sort;

      this.paintFacets(root, { cat, types, brands, categories, typeCounts, brandCounts });
      this.paintActive(root, { cat, types, brands });

      const grid = root.querySelector('[data-machine-grid]');
      if (grid) grid.replaceChildren(...slice.map(card));

      const filtered = !!(cat || types.length || brands.length);
      this.paintFallback(sorted.length === 0 && !filtered && this.terms ? vehicle : null, this.terms);

      const empty = root.querySelector('[data-machine-empty]');
      if (empty) {
        empty.hidden = sorted.length > 0;
        const name = empty.querySelector('[data-machine-empty-name]');
        const termsEl = empty.querySelector('[data-machine-empty-terms]');
        if (name) name.textContent = vehicle.label;
        if (termsEl) termsEl.textContent = this.terms;
        const all = empty.querySelector('[data-machine-empty-all]');
        if (all) all.href = this.url({ scope: null, vehicle: null, cat: null, page: null });
        const clear = empty.querySelector('[data-machine-empty-clear]');
        if (clear) { clear.hidden = !(cat || types.length || brands.length); clear.href = this.url({ cat: null, ptype: null, brand: null, page: null }); }
      }

      const pagination = root.querySelector('[data-machine-pagination]');
      const summary = root.querySelector('[data-machine-summary]');
      if (pagination) {
        pagination.replaceChildren();
        pagination.hidden = pages <= 1;
        if (pages > 1) {
          const link = (n, label, cls) => {
            const a = document.createElement('a');
            a.className = cls;
            a.href = this.url({ page: n === 1 ? null : String(n) });
            a.dataset.machinePage = String(n);
            a.textContent = label;
            return a;
          };
          if (current > 1) pagination.appendChild(link(current - 1, 'Previous', 'pagination__link pagination__link--prev'));
          const ul = document.createElement('ul');
          ul.className = 'pagination__pages';
          for (let n = 1; n <= pages; n++) {
            const li = document.createElement('li');
            if (n === current) {
              const span = document.createElement('span');
              span.className = 'pagination__page pagination__page--current';
              span.setAttribute('aria-current', 'page');
              span.textContent = String(n);
              li.appendChild(span);
            } else if (Math.abs(n - current) <= 2 || n === 1 || n === pages) {
              li.appendChild(link(n, String(n), 'pagination__page'));
            } else if (Math.abs(n - current) === 3) {
              const gap = document.createElement('span');
              gap.className = 'pagination__page pagination__page--gap';
              gap.textContent = '…';
              li.appendChild(gap);
            } else continue;
            ul.appendChild(li);
          }
          pagination.appendChild(ul);
          if (current < pages) pagination.appendChild(link(current + 1, 'Next', 'pagination__link pagination__link--next'));
        }
      }
      if (summary) summary.textContent = sorted.length ? `Page ${current} of ${pages} · ${sorted.length} parts` : '';

      root.removeAttribute('aria-busy');
    }

    /* ---- The sidebar ---- */

    /**
     * The category tree and the brand list, in the collection sidebar's
     * markup. A category is a link that applies it, one at a time; the open
     * one lists its product types as checkboxes beneath it. A value that is
     * applied stays listed even at zero, so it can be removed.
     */
    paintFacets(root, { cat, types, brands, categories, typeCounts, brandCounts }) {
      const text = (className, content) => {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = content;
        return span;
      };
      // The label on one line with its full name as the tooltip (collection.css cuts it).
      const labelText = (s) => { const el = text('facets__value-label', s); el.title = s; return el; };
      const checkbox = (className, dataName, value, checked, n) => {
        const li = document.createElement('li');
        const label = document.createElement('label');
        label.className = 'facets__value';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset[dataName] = value;
        input.checked = checked;
        label.append(input, labelText(value), text('facets__value-count', String(n)));
        li.append(label);
        return li;
      };
      const byName = (a, b) => String(a).localeCompare(String(b));

      const tree = root.querySelector('[data-machine-tree]');
      if (tree) {
        const names = [...categories.keys()].sort(byName);
        if (cat && !categories.has(cat)) names.push(cat);
        tree.replaceChildren(...names.map((name) => {
          const open = name === cat;
          const li = document.createElement('li');
          li.className = `facets__branch${open ? ' facets__branch--open' : ''}`;
          li.dataset.facetBranch = name;
          const link = document.createElement('a');
          link.className = 'facets__branch-link';
          link.href = this.url({ cat: open ? null : name, ptype: null, page: null });
          link.dataset.machineCategory = name;
          if (open) link.setAttribute('aria-current', 'true');
          const chevron = document.createElement('span');
          chevron.className = 'facets__branch-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          link.append(labelText(name), text('facets__value-count', String(categories.get(name) || 0)), chevron);
          li.append(link);
          if (open) {
            const leaves = document.createElement('ul');
            leaves.className = 'facets__values facets__leaves';
            leaves.dataset.facetLeaves = '';
            const typeNames = [...typeCounts.keys()].sort(byName);
            for (const t of types) if (!typeCounts.has(t)) typeNames.push(t);
            for (const t of typeNames) leaves.append(checkbox('facets__value', 'machineType', t, types.includes(t), typeCounts.get(t) || 0));
            if (typeNames.length) li.append(leaves);
          }
          return li;
        }));
      }
      const activeCount = root.querySelector('[data-machine-active-count]');
      if (activeCount) {
        const n = (cat ? 1 : 0) + types.length;
        activeCount.textContent = String(n);
        activeCount.hidden = n === 0;
      }

      const list = root.querySelector('[data-machine-brands]');
      const group = root.querySelector('[data-machine-brands-group]');
      if (list) {
        const names = [...brandCounts.keys()].sort((a, b) => (brandCounts.get(b) - brandCounts.get(a)) || byName(a, b));
        for (const b of brands) if (!brandCounts.has(b)) names.push(b);
        list.replaceChildren(...names.map((name) => checkbox('facets__value', 'machineBrand', name, brands.includes(name), brandCounts.get(name) || 0)));
        // One brand is no choice (Lucas, 2026-10-12) — unless it is applied.
        if (group) group.hidden = names.length < 2 && brands.length === 0;
      }
      const brandsCount = root.querySelector('[data-machine-brands-count]');
      if (brandsCount) {
        brandsCount.textContent = String(brands.length);
        brandsCount.hidden = brands.length === 0;
      }
    }

    /** "Filtered by" and a tag per applied value, each a link that removes it — the collection page's row. */
    paintActive(root, { cat, types, brands }) {
      const row = root.querySelector('[data-machine-active]');
      if (!row) return;
      const applied = [
        ...(cat ? [{ label: cat, kind: 'Category', href: this.url({ cat: null, ptype: null, page: null }) }] : []),
        ...types.map((t) => ({ label: t, kind: 'Product type', href: this.url({ ptype: types.filter((x) => x !== t), page: null }) })),
        ...brands.map((b) => ({ label: b, kind: 'Brand', href: this.url({ brand: brands.filter((x) => x !== b), page: null }) })),
      ];
      row.hidden = applied.length === 0;
      if (!applied.length) { row.replaceChildren(); return; }
      const label = document.createElement('li');
      label.className = 'collection__active-label';
      label.setAttribute('aria-hidden', 'true');
      label.textContent = 'Filtered by';
      const items = applied.map((f) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'collection__filter';
        a.href = f.href;
        a.dataset.machineLink = '';
        a.setAttribute('aria-label', `Remove ${f.kind}: ${f.label}`);
        const name = document.createElement('span');
        name.className = 'collection__filter-label';
        name.textContent = f.label;
        const x = document.createElement('span');
        x.className = 'collection__filter-remove';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        a.append(name, x);
        li.append(a);
        return li;
      });
      const clear = document.createElement('li');
      const clearLink = document.createElement('a');
      clearLink.className = 'collection__clear';
      clearLink.href = this.url({ cat: null, ptype: null, brand: null, page: null });
      clearLink.dataset.machineLink = '';
      clearLink.textContent = 'Clear filters';
      clear.append(clearLink);
      row.replaceChildren(label, ...items, clear);
    }
  }

  if (!customElements.get('search-page')) customElements.define('search-page', SearchPage);

  window.SearchPage = { tokens, matches, score, loosen, categoryOf, sortProducts, outOfStockLast, card, cardOptions };
})();
